import { Component, computed, effect, inject, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatPrefix, MatSuffix } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IwahEnvironment, IwahFolderMeta, IwahJobMeta, SearchService } from '@iwah/core';
import { LoadingDotsModule } from '@psu-pennstateit-swe/components/loading-dots';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';

@Component({
  selector: 'iwah-searchbar',
  imports: [
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatOption,
    MatPrefix,
    MatSuffix,
    MatTooltip,
    ReactiveFormsModule,
    LoadingDotsModule,
  ],
  templateUrl: './searchbar.component.html',
  styleUrl: './searchbar.component.scss',
})
export class SearchbarComponent {
  @ViewChild('trigger') quickResults!: MatAutocompleteTrigger;
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly searchService = inject(SearchService);

  searchBarForm = new FormGroup({
    searchBarField: new FormControl(''),
  });

  private readonly routeSearchQuery = toSignal(
    this.route.queryParamMap.pipe(map((params) => params.get('search') ?? '')),
    { initialValue: this.route.snapshot.queryParamMap.get('search') ?? '' },
  );

  searchQuery = toSignal(
    this.searchBarForm.controls.searchBarField.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map((query) => (query && query.length >= 3 ? query : '')),
    ),
    { initialValue: '' },
  );

  searchResultsProdResource = this.searchService.getSearchResultResource(this.searchQuery);
  searchResultsTestResource = this.searchService.getSearchResultResource(this.searchQuery, { environment: 'TEST' });

  searchResultsCombined = computed(() => {
    const prodResults = this.searchResultsProdResource.hasValue() ? this.searchResultsProdResource.value() : [];
    const testResults = this.searchResultsTestResource.hasValue() ? this.searchResultsTestResource.value() : [];

    const results: {
      prod: { environment: 'PROD'; results: (IwahFolderMeta | IwahJobMeta)[] };
      test: { environment: 'TEST'; results: (IwahFolderMeta | IwahJobMeta)[] };
    } = {
      prod: {
        environment: 'PROD',
        results: [],
      },
      test: {
        environment: 'TEST',
        results: [],
      },
    };

    if (prodResults.length === 5) {
      results.prod.results = [...prodResults];
    } else {
      const prodResultsLength = prodResults.length;
      results.prod.results = [...prodResults];

      results.test.results = [...testResults].slice(0, 5 - prodResultsLength);
    }

    return results;
  });

  constructor() {
    effect(() => {
      const routeSearchQuery = this.routeSearchQuery();

      if (this.searchBarForm.controls.searchBarField.value !== routeSearchQuery) {
        this.searchBarForm.controls.searchBarField.setValue(routeSearchQuery, { emitEvent: false });
      }
    });
  }

  async navigateToPath(item: IwahFolderMeta | IwahJobMeta, environment: IwahEnvironment) {
    const basePath = environment === 'PROD' ? '/jobs' : '/jobs/test';

    /* Remove the leading slash character from the folder path to keep the URL human-readable and free of encoded
     * slashes as much as possible */
    if (this.isFolder(item)) {
      await this.router.navigate([basePath, item.path.slice(1)]);
    } else {
      await this.router.navigate([basePath, item.parentPath.slice(1), item.name]);
    }
  }

  isFolder(item: IwahFolderMeta | IwahJobMeta): item is IwahFolderMeta {
    return 'folderType' in item;
  }

  async onClear() {
    this.searchBarForm.reset({ searchBarField: '' });
    if (this.searchResultsProdResource.hasValue()) {
      this.searchResultsProdResource.value.set([]);
    }
    if (this.searchResultsTestResource.hasValue()) {
      this.searchResultsTestResource.value.set([]);
    }
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  async onSubmit() {
    if (this.searchBarForm.controls.searchBarField.value) {
      await this.router.navigate(['/jobs'], {
        queryParams: { search: this.searchBarForm.controls.searchBarField.value },
      });
      this.quickResults.closePanel();
    }
  }

  async onQuickResultSelected(item: IwahFolderMeta | IwahJobMeta, environment: IwahEnvironment) {
    this.searchBarForm.controls.searchBarField.setValue('');
    this.quickResults.closePanel();
    await this.navigateToPath(item, environment);
  }
}
